import type { CSSProperties } from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import clsx from 'clsx'
import styled, { keyframes } from 'styled-components'
import Clickable from 'components/Common/Clickable'
import noop from 'lib/function/noop'

const progressAnimation = keyframes`
  from {
    transform: translateX(var(--pagination-dot-progress-start, -100%));
  }
  to {
    transform: translateX(0%);
  }
`

const DotsContainer = styled.div`
  --pagination-dot-progress-start: -100%;

  display: flex;

  [dir="rtl"] & {
    --pagination-dot-progress-start: 100%;
  }
  
  &.position-center {
    justify-content: center;
  }
  
  &.position-start {
    justify-content: start;
  }

  /* Default variant */
  --pagination-dot-bg: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
  --pagination-dot-fill: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  --pagination-dot-border-thickness: 0;

  &.variant-outlined {
  --pagination-dot-bg: ${props => props.theme.palette.neutral.surface.normalBackground};
  --pagination-dot-fill: ${props => props.theme.palette.neutral.surface.normalContrast};
  --pagination-dot-border-thickness: 1px;
  }
`

const DotClickable = styled(Clickable)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-inline: ${rem(2)};
  transition: padding 0.3s ease;
  min-height: ${rem(16)};

  &:not(.visible) {
    padding-inline: 0;
  }
`

const Dot = styled.div`
  position: relative;
  height: ${rem(8)};
  width: ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: var(--pagination-dot-bg);
  border: var(--pagination-dot-border-thickness) solid ${props => props.theme.palette.neutral.surface.normalContrast};
  overflow: hidden;
  transform: translateZ(0);
  transition: width 0.3s ease, height 0.3s ease;

  &.active {
    width: ${rem(16)};

    &::after {
      animation: ${progressAnimation} var(--pagination-dot-duration) linear forwards;
      animation-play-state: var(--pagination-dot-play-state, running);
    }
  }

  &.taper1 {
    height: ${rem(6)};
    width: ${rem(6)};
  }

  &.taper2 {
    height: ${rem(4)};
    width: ${rem(4)};
  }

  &:not(.visible) {
    height: 0;
    width: 0;
  }

  &::after {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    inset-inline-start: 0;
    width: 100%;
    height: 100%;
    background-color: var(--pagination-dot-fill);
    transform: translateX(var(--pagination-dot-progress-start));
  }
`

type Variant = 'default' | 'outlined'

interface Props {
  count: number
  activeIndex: number
  /** Duration in milliseconds that each slide is displayed (set to 0 for no animation) */
  slideDuration?: number
  /** Maximum number of dots to show before tapering off (default 7) */
  visibleLimit?: number
  onSelect?: (index: number) => void
  /** Whether the progress animation is paused */
  paused?: boolean
  /** Called with `true` on mouse enter, `false` on mouse leave */
  onHoverChange?: (hovered: boolean) => void
  /** @default 'default' */
  variant?: Variant
  position?: 'start' | 'center'
  className?: string
}

function PaginationDots({
  count,
  activeIndex,
  slideDuration = 0,
  visibleLimit = 7,
  onSelect,
  paused,
  onHoverChange = noop,
  variant = 'default',
  position = 'center',
  className,
}: Props) {
  const intl = useIntl()
  const handlePointerEnter = useCallback<React.PointerEventHandler<HTMLDivElement>>((e) => {
    if (e.pointerType === 'mouse') {
      onHoverChange(true)
    }
  }, [onHoverChange])

  const handlePointerLeave = useCallback<React.PointerEventHandler<HTMLDivElement>>((_e) => {
    onHoverChange(false)
  }, [onHoverChange])

  const style = {
    '--pagination-dot-duration': `${slideDuration}ms`,
    '--pagination-dot-play-state': paused ? 'paused' : 'running',
  } as CSSProperties

  const getSlideAriaLabel = useCallback((index: number) => intl.formatMessage(
    {
      id: 'ugx7FCBcHXuwnECX5x9O',
      defaultMessage: 'Go to slide {index}',
      description: 'Aria label for selecting a specific pagination dot slide',
    },
    { index: index + 1 },
  ), [intl])

  let visibleStart = 0
  let visibleEnd = count
  if (count > visibleLimit) {
    visibleStart = activeIndex - Math.floor(visibleLimit / 2)
    visibleEnd = visibleStart + visibleLimit
    if (visibleStart < 0) {
      visibleStart = 0
      visibleEnd = visibleLimit
    }
    if (visibleEnd > count) {
      visibleStart = count - visibleLimit
      visibleEnd = count
    }
  }

  return (
    <DotsContainer
      className={clsx(`variant-${variant}`, `position-${position}`, className)}
      style={style}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      {Array.from({ length: count }, (_, index) => {
        const active = index === activeIndex
        const visible = index >= visibleStart && index < visibleEnd
        const distanceFromEndOfVisible = Math.min(
          index - visibleStart,
          (visibleEnd - 1) - index,
        )
        const distanceFromEndOfRange = Math.min(index, (count - 1) - index)

        return (
          <DotClickable
            key={index}
            onClick={() => onSelect?.(index)}
            aria-label={getSlideAriaLabel(index)}
            aria-current={active ? 'true' : undefined}
            aria-hidden={!visible}
            tabIndex={visible ? undefined : -1}
            className={clsx({
              active,
              visible,
            })}
          >
            <Dot
              className={clsx({
                active,
                visible,
                taper1: !active && distanceFromEndOfRange > 1 && distanceFromEndOfVisible === 1,
                taper2: !active && distanceFromEndOfRange > 0 && distanceFromEndOfVisible < 1,
              })}
            />
          </DotClickable>
        )
      })}
    </DotsContainer>
  )
}

export default PaginationDots
